import { forwardRef, type ReactNode, type InputHTMLAttributes, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { WarningCircle } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

// ─── Shared styles ───────────────────────────────────────────

// min-w-0 / max-w-full / appearance-none: `w-full` alone does not hold a native date or time
// control. Those two have an INTRINSIC width from the browser's own picker chrome, which beat
// w-full and rendered Date and Start time visibly wider than Matter, Hearing type and Estimated
// duration in the same column. Dropping the native appearance lets them size like every other
// field; the explicit h-9 below keeps their height, which is what collapses without it.
const inputBase =
  'w-full min-w-0 max-w-full appearance-none rounded-md border bg-card px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 transition-colors';

const inputNormal = 'border-border focus:ring-brand-600/40 focus:border-brand-600';
const inputError = 'border-red-400 focus:ring-red-500/40 focus:border-red-500 bg-red-50/30 dark:bg-red-500/10 dark:border-red-500/50';

// ─── FormField wrapper ───────────────────────────────────────

interface FormFieldProps {
  label: string;
  error?: string | null;
  required?: boolean;
  hint?: string;
  children: ReactNode;
  className?: string;
}

export function FormField({ label, error, required, hint, children, className }: FormFieldProps) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <label className="block text-xs font-medium text-muted-foreground">
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </label>
      {children}
      {error && (
        <div className="flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400 animate-in fade-in slide-in-from-top-1 duration-200">
          <WarningCircle className="h-3 w-3 shrink-0" weight="fill" />
          <span>{error}</span>
        </div>
      )}
      {!error && hint && (
        <p className="text-xs text-muted-foreground">{hint}</p>
      )}
    </div>
  );
}

// ─── Input with built-in error styling ───────────────────────

interface InputFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> {
  label: string;
  error?: string | null;
  required?: boolean;
  hint?: string;
  containerClassName?: string;
}

export const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
  ({ label, error, required, hint, containerClassName, ...inputProps }, ref) => {
    return (
      <FormField label={label} error={error} required={required} hint={hint} className={containerClassName}>
        <input
          ref={ref}
          className={cn(inputBase, 'h-9', error ? inputError : inputNormal)}
          {...inputProps}
        />
      </FormField>
    );
  }
);
InputField.displayName = 'InputField';

// ─── Select with built-in error styling ──────────────────────

interface SelectFieldProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'className'> {
  label: string;
  error?: string | null;
  required?: boolean;
  hint?: string;
  containerClassName?: string;
  children: ReactNode;
}

export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(
  ({ label, error, required, hint, containerClassName, children, ...selectProps }, ref) => {
    return (
      <FormField label={label} error={error} required={required} hint={hint} className={containerClassName}>
        <select
          ref={ref}
          className={cn(inputBase, 'h-9', error ? inputError : inputNormal)}
          {...selectProps}
        >
          {children}
        </select>
      </FormField>
    );
  }
);
SelectField.displayName = 'SelectField';

// ─── Textarea with built-in error styling ────────────────────

interface TextareaFieldProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'> {
  label: string;
  error?: string | null;
  required?: boolean;
  hint?: string;
  containerClassName?: string;
}

export const TextareaField = forwardRef<HTMLTextAreaElement, TextareaFieldProps>(
  ({ label, error, required, hint, containerClassName, ...textareaProps }, ref) => {
    return (
      <FormField label={label} error={error} required={required} hint={hint} className={containerClassName}>
        <textarea
          ref={ref}
          className={cn(inputBase, 'py-2 resize-none', error ? inputError : inputNormal)}
          {...textareaProps}
        />
      </FormField>
    );
  }
);
TextareaField.displayName = 'TextareaField';
